import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../useAuth'; // Adjust the path to your useAuth hook
import { useTranslation } from 'react-i18next'; // For dynamic language setting

/**
 * Renders the Dialogflow Messenger component (df-messenger).
 * Dynamically loads the necessary bootstrap script if not already present.
 * Personalizes the chat by passing user ID and parameters if a user is logged in.
 * Sets the language code based on the current i18n language.
 *
 * @component
 * @param {object} props - Component props.
 * @param {string} [props.chatTitle='Support'] - The title displayed in the chat header.
 * @param {string} [props.agentId='YOUR_AGENT_ID'] - Your Dialogflow Agent ID. **REQUIRED**
 * @param {string} [props.welcomeIntent='WELCOME'] - The intent triggered when the chat opens.
 * @example
 * return <DialogflowMessenger agentId="YOUR_AGENT_ID_HERE" chatTitle="ProRecruit Help" />
 */
const DialogflowMessenger = ({
    // Provide default props for common attributes
    chatTitle = "Support",
    // IMPORTANT: Replace with your actual Dialogflow Agent ID
    agentId = "1e30c295-3dc3-4906-87d9-1b2a1890d3d3",
    welcomeIntent = "WELCOME",
}) => {
    const { currentUser } = useAuth(); // Get user info from context
    const { i18n } = useTranslation(); // Get i18n instance

    // Determine language code, mapping if necessary and providing fallback
    const languageCode = useMemo(() => {
        const currentLang = i18n.language;
        // Simplify language code (e.g., 'en-US' -> 'en')
        const baseLang = currentLang.split('-')[0];
        // Add more supported Dialogflow language codes as needed
        const supportedCodes = ['en', 'fr'];
        return supportedCodes.includes(baseLang) ? baseLang : 'en'; // Default to English
    }, [i18n.language]);

    // Generate a stable user ID (Firebase UID or anonymous)
    const userId = useMemo(() => {
        return currentUser?.uid || `anonymous_${Math.random().toString(36).substring(2, 15)}`;
    }, [currentUser]);

    // Prepare user parameters for personalization within Dialogflow
    const userParams = useMemo(() => {
        if (currentUser) {
            return {
                // Parameters Dialogflow can potentially use in fulfillment/context
                displayName: currentUser.displayName || 'Valued User',
                email: currentUser.email || '',
                // Add any other relevant non-sensitive parameters
                // Example: userSegment: 'trialUser', // if you have this info
            };
        }
        return {}; // Empty object for anonymous users
    }, [currentUser]);

    // Stringify params only once when they change
    const stringifiedUserParams = useMemo(() => JSON.stringify(userParams), [userParams]);

    // Effect for loading the Dialogflow bootstrap script
    useEffect(() => {
        const scriptId = 'dialogflow-messenger-script';
        // eslint-disable-next-line no-unused-vars
        const dfMessengerElement = document.querySelector('df-messenger');

        // Only load script if custom element isn't defined AND script isn't already added
        if (!customElements.get('df-messenger') && !document.getElementById(scriptId)) {
            console.log("Loading Dialogflow Messenger script...");
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://www.gstatic.com/dialogflow-console/fast/messenger/bootstrap.js?v=1';
            script.async = true;
            document.body.appendChild(script);

            // Cleanup function: remove the script added by THIS instance
            return () => {
                const existingScript = document.getElementById(scriptId);
                if (existingScript && existingScript.parentNode) {
                    console.log("Cleaning up Dialogflow Messenger script...");
                    existingScript.parentNode.removeChild(existingScript);
                    // Optional: If you want to ensure the element itself is removed on unmount
                    // const messengerElement = document.querySelector('df-messenger');
                    // if (messengerElement && messengerElement.parentNode) {
                    //    messengerElement.parentNode.removeChild(messengerElement);
                    // }
                }
            };
        } else {
            console.log("Dialogflow Messenger script or element already present.");
        }
        // No cleanup needed if script wasn't added by this instance
    }, []); // Empty dependency array ensures this runs only once on mount

    // Effect to update attributes if key props/context change after initial mount
    // This ensures personalization updates if the user logs in/out or language changes
    useEffect(() => {
        const messengerElement = document.querySelector('df-messenger');
        if (messengerElement) {
             console.log("Updating df-messenger attributes...");
            messengerElement.setAttribute('language-code', languageCode);
            messengerElement.setAttribute('user-id', userId);
            messengerElement.setAttribute('user-params', stringifiedUserParams);
            messengerElement.setAttribute('chat-title', chatTitle);
            messengerElement.setAttribute('agent-id', agentId);
            messengerElement.setAttribute('intent', welcomeIntent);
        }
     }, [languageCode, userId, stringifiedUserParams, chatTitle, agentId, welcomeIntent]); // Re-run if these change


    // Render the df-messenger element. Attributes will be updated by the useEffect above.
    // We don't need to pass props directly here again, as the useEffect handles updates.
    return (
        <df-messenger></df-messenger>
        // Alternatively, set initial attributes directly, but useEffect is safer for updates:
        /*
        <df-messenger
            intent={welcomeIntent}
            chat-title={chatTitle}
            agent-id={agentId}
            language-code={languageCode}
            user-id={userId}
            user-params={stringifiedUserParams}
        ></df-messenger>
        */
    );
};

DialogflowMessenger.propTypes = {
    /** The title displayed in the Dialogflow Messenger chat header. */
    chatTitle: PropTypes.string,
    /** Your Dialogflow Agent ID (e.g., "1e30c295-...") */
    agentId: PropTypes.string.isRequired, // Make agentId required
    /** The initial intent to trigger when the chat opens. */
    welcomeIntent: PropTypes.string,
    // userId and userParams are now derived internally from context, so no longer props.
};

export default DialogflowMessenger;